import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Mail } from 'lucide-react';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    async function handleUnsubscribe() {
      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid unsubscribe link');
        setLoading(false);
        return;
      }

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/unsubscribe?token=${token}`, {
          method: 'GET',
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || result.message || 'Failed to unsubscribe');
        }

        setSuccess(true);
        setEmail(result.email);
      } catch (err) {
        console.error('Unsubscribe error:', err);
        setError(err instanceof Error ? err.message : 'Failed to process unsubscribe request');
      } finally {
        setLoading(false);
      }
    }

    handleUnsubscribe();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Processing your request...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Unsubscribe Failed
          </h1>

          <p className="text-gray-600 text-center mb-6">{error}</p>

          <Link
            to="/"
            className="block w-full py-3 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 transition-colors text-center"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Successfully Unsubscribed
          </h1>

          {email && (
            <p className="text-gray-600 text-center mb-6">
              <span className="font-medium">{email}</span> has been removed from our job alerts mailing list.
            </p>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-900 font-medium mb-1">
                  You will no longer receive:
                </p>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>New job posting notifications</li>
                  <li>Job alert emails</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 text-center mb-6">
            You can resubscribe at any time from our homepage or load board.
          </p>

          <div className="space-y-3">
            <Link
              to="/job-seekers"
              className="block w-full py-3 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 transition-colors text-center"
            >
              Browse Workers
            </Link>
            <Link
              to="/"
              className="block w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors text-center"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
